import { createApp } from "vue";
import PlanningApp from "@/vue/planning/App.vue"
import store from "@/vue/planning/store/store";
import ResidenceGallery from "@/vue/residence-gallery/App.vue";
import VCalendar from 'v-calendar';
import MapApp from "@/vue/map/App-v2.vue";
import VueGoogleMaps from "@fawmi/vue-google-maps"
import StationApp from "@/vue/station-domain/App.vue";
import ReviewApp from "@/vue/reviews/App.vue";
import storeReview from "@/vue/reviews/store/store.js";
import ClubAdvantagesApp from "@/vue/club-advantages/App.vue"
import TrainBanner from "@/vue/train-banner/App.vue"
import ReviewTranslationApp   from "@/vue/reviews-translation/App.vue";
import storeReviewTranslation from "@/vue/reviews-translation/store/store.js";

const API_KEY = 'AIzaSyCOthaijtwQIXqc-nv6DRcEuUg62DqjheY';
const MAP_ID  = '326e7548c437d85e';

export default class AppInitializer {
	static singleton;


	constructor() {
		if (AppInitializer.singleton) {
			return AppInitializer.singleton
		}
		this._planningApp = null
		this._planningAppLock = null
		this._mainMapApp = null
		this._mainMapAppLock = null
		this._residenceGallery = null
		this._residenceGalleryLock = null
		this._stationDomainMap = null
		this._stationDomainMapLock = null
		this._reviewApp = null
		this._reviewAppLock = null
		this._clubAdvantagesApp = null
		this._clubAdvantagesAppLock = null
		this._trainBanner = null
		this._trainBannerLock = null
		
		AppInitializer.singleton = this
		return AppInitializer.singleton
	}
	get planningApp() {
		return this._planningApp
	}
	get mainMapApp() {
		return this._mainMapApp
	}
	get stationDomainMap() {
		return this._stationDomainMap
	}
	get reviewApp() {
		return this._reviewApp
	}
	get clubAdvantagesApp() {
		return this._clubAdvantagesApp
	}
	get residenceGallery() {
		return this._residenceGallery
	}
	get trainBanner() {
		return this._trainBanner
	}


	isAllAppsInitialized() {
		return [
			this._planningApp,
			this._mainMapApp,
			this._stationDomainMap,
			this._reviewApp,
		].every(Boolean);
	}

	async _runOnce(instanceProp, lockProp, run) {
		if (this[instanceProp]) return this[instanceProp]
		if (!this[lockProp]) {
			this[lockProp] = (async () => {
				try {
					await run()
					return this[instanceProp]
				} finally {
					this[lockProp] = null
				}
			})()
		}
		return this[lockProp]
	}

	_mountIfPresent(selector, app) {
		if (!document.querySelector(selector)) {
			return false
		}
		app.mount(selector)
		return true
	}

	/**
	 * @param {*} keyUpdater
	 */
	async initPlanningApp(keyUpdater) {
		return this._runOnce("_planningApp", "_planningAppLock", async () => {
			const app = createApp(PlanningApp, { keyUpdater })
			app.use(store)
			app.use(VCalendar)
			if (this._mountIfPresent("#planner-app", app)) {
				this._planningApp = app
			}
		})
	}

	async forceAppReInit(appName, keyUpdater) {
		switch (appName) {
		  case 'planning':
			if (this._planningAppLock) await this._planningAppLock
			if (this._planningApp) {
			  // Vue3 unmount
			  this._planningApp.unmount()
			  this._planningApp = null
			}
			// Re-init with the (possibly new) keyUpdater
			await this.initPlanningApp(keyUpdater)
			break
	
		  default:
			throw new Error(`Re-init for "${appName}" not implemented`)
		}
		
	}
	async initMainMapApp() {
		return this._runOnce("_mainMapApp", "_mainMapAppLock", async () => {
			const mapApp = createApp(MapApp);
			mapApp.use(VueGoogleMaps, {
				load: {
				  key: API_KEY,
				  mapId: MAP_ID,
				  v: 'weekly'        // ← get off of the retired 3.47 release
				},
				installComponents: true
			});
			if (this._mountIfPresent("#tf-map--wrapper", mapApp)) {
				this._mainMapApp = mapApp
			}
		})
	}

	async initStationDomainApp() {
		return this._runOnce("_stationDomainMap", "_stationDomainMapLock", async () => {
			const stationApp = createApp(StationApp)
			if (this._mountIfPresent("#station-app", stationApp)) {
				this._stationDomainMap = stationApp
			}
		})
	}

	async initReviewApp() {
		return this._runOnce("_reviewApp", "_reviewAppLock", async () => {
			const script = document.getElementById('product-js');
			const isTranslation = script?.dataset.reviewTranslation === 'true';
			console.log(`⚙️ initReviewApp – translation mode: ${isTranslation}`);

			const storeToUse = isTranslation
				? storeReviewTranslation
				: storeReview;
			storeToUse.commit('setScreenType',
				window.screenSizeManager.screenType);
			const RootComponent = isTranslation
				? ReviewTranslationApp
				: ReviewApp

			console.log('⚙️ Root component:', isTranslation ? 'ReviewTranslationApp' : 'ReviewApp');
			const reviewApp = createApp(RootComponent)
			reviewApp.use(storeToUse)
			if (this._mountIfPresent("#review-app", reviewApp)) {
				this._reviewApp = reviewApp
			}
		})
	}

	async initResidenceGalerryApp() {
		return this._runOnce("_residenceGallery", "_residenceGalleryLock", async () => {
			const galleryApp = createApp(ResidenceGallery)
			if (this._mountIfPresent("#residence-gallery-app", galleryApp)) {
				this._residenceGallery = galleryApp
			}
		})
	}

	async initClubAdvantagesApp() {
		return this._runOnce("_clubAdvantagesApp", "_clubAdvantagesAppLock", async () => {
			const clubAdvantagesApp = createApp(ClubAdvantagesApp)
			if (this._mountIfPresent("#club-advantages-app", clubAdvantagesApp)) {
				this._clubAdvantagesApp = clubAdvantagesApp
			}
		})
	}

	// TODO: remove this app - initialize it in the main.js file
	async initTrainBannerApp() {
		return this._runOnce("_trainBanner", "_trainBannerLock", async () => {
			const trainBanner = createApp(TrainBanner)
			if (this._mountIfPresent("#trainBanner", trainBanner)) {
				this._trainBanner = trainBanner
			}
		})
	}

	async initAll() {
			await Promise.all([
				this.initPlanningApp(),
				this.initMainMapApp(),
			    this.initStationDomainApp(),
				this.initReviewApp(),

			])
		}
	
}
